import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ImmuneCell {
  name: string;
  role: string;
  icon: string;
  color: string;
}

interface VaccineType {
  name: string;
  mechanism: string;
  example: string;
  icon: string;
}

interface Milestone {
  year: string;
  event: string;
  significance: string;
}

interface ImmuneFact {
  label: string;
  icon: string;
}

interface VaccineImmuneTemplateProps {
  topic: string;
  subtitle: string;
  immuneCells: ImmuneCell[];
  vaccineTypes: VaccineType[];
  milestones: Milestone[];
  immuneFacts: ImmuneFact[];
  responseStages: { stage: string; time: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: VaccineImmuneTemplateProps = {
  topic: '疫苗与免疫系统',
  subtitle: 'Vaccines & Immune System',
  immuneCells: [
    {
      name: 'T细胞',
      role: '识别并摧毁被感染的细胞',
      icon: '🛡️',
      color: '#22c55e',
    },
    {
      name: 'B细胞',
      role: '产生抗体中和病原体',
      icon: '🔬',
      color: '#4ade80',
    },
    {
      name: '巨噬细胞',
      role: '吞噬并消化外来病原体',
      icon: '🦠',
      color: '#86efac',
    },
    {
      name: 'NK细胞',
      role: '天然杀伤细胞，先天免疫屏障',
      icon: '⚔️',
      color: '#bbf7d0',
    },
  ],
  vaccineTypes: [
    {
      name: '减毒活疫苗',
      mechanism: '使用弱化的活病原体',
      example: '麻疹/水痘/卡介苗',
      icon: '💉',
    },
    {
      name: '灭活疫苗',
      mechanism: '使用已杀灭的完整病原体',
      example: '流感/狂犬病/甲肝',
      icon: '🧪',
    },
    {
      name: 'mRNA疫苗',
      mechanism: '指导细胞制造抗原蛋白',
      example: 'COVID-19 (辉瑞/莫德纳)',
      icon: '🧬',
    },
    {
      name: '亚单位疫苗',
      mechanism: '仅含病原体特定蛋白片段',
      example: '乙肝/HPV/百日咳',
      icon: '🔩',
    },
  ],
  milestones: [
    { year: '1796', event: '天花疫苗', significance: '爱德华·詹纳开创疫苗接种' },
    { year: '1955', event: '脊髓灰质炎疫苗', significance: '乔纳斯·索尔克拯救数百万儿童' },
    { year: '1981', event: '乙肝疫苗', significance: '首个基因工程疫苗' },
    { year: '2020', event: 'mRNA疫苗', significance: '史上最快疫苗研发（11个月）' },
  ],
  immuneFacts: [
    { label: '抗原呈递', icon: '🎯' },
    { label: '免疫记忆', icon: '🧠' },
    { label: '群体免疫', icon: '👥' },
    { label: '佐剂增强', icon: '⚡' },
  ],
  responseStages: [
    { stage: '抗原识别', time: '0-4小时' },
    { stage: '先天免疫', time: '4-96小时' },
    { stage: '适应性免疫', time: '4-7天' },
    { stage: '免疫记忆', time: '数月-终生' },
  ],
  stats: [
    { value: '200+', label: '年疫苗研发历史' },
    { value: '25亿', label: '每年全球疫苗接种剂次' },
    { value: '天花', label: '人类唯一根除的传染病' },
  ],
  bottomQuote:
    '疫苗是人类医学史上最伟大的发明之一——它以微小的代价换来了对致命传染病的持久免疫',
};

export const VaccineImmuneTemplate: React.FC<
  Partial<VaccineImmuneTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const cellStart = 30;
  const vaccineStart = 160;
  const svgStart = 200;
  const milestoneStart = 300;
  const factStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const antibodyDraw = interpolate(
    frame,
    [svgStart, svgStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #052e16 0%, #022c22 50%, #011d19 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating cell-like particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const y = (i * 173) % 1080;
        const size = 4 + (i % 7);
        const drift = Math.sin(frame * 0.025 + i) * 10;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.6);
        const colors = ['#22c55e', '#4ade80', '#86efac', '#bbf7d0'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              border: `1px solid ${colors[i % colors.length]}40`,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}30`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#15803d',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#bbf7d0',
            flexShrink: 0,
          }}
        >
          💉 免疫科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#dcfce7',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Immune Cells ===== */}
      <div style={{ position: 'absolute', top: 115, left: 36, width: 520 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          免疫细胞
        </div>
        {p.immuneCells.map((cell, i) => {
          const s = spring({
            frame: frame - (cellStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                background: '#0a2818',
                borderRadius: 14,
                padding: '12px 16px',
                border: `1.5px solid ${cell.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateX(${(1 - s) * 30}px)`,
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: `${cell.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                  flexShrink: 0,
                  boxShadow: `0 0 18px ${cell.color}30`,
                }}
              >
                {cell.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color: cell.color }}>
                    {cell.name}
                  </span>
                </div>
                <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}>
                  {cell.role}
                </div>
              </div>
            </div>
          );
        })}

        {/* Immune Response Stages */}
        <div style={{ marginTop: 30 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: -40,
              letterSpacing: '0.08em',
            }}
          >
            免疫应答阶段
          </div>
          <svg width="580" height="230" viewBox="0 0 580 230">
            {p.responseStages.map((rs, i) => {
              const s = spring({
                frame: frame - (cellStart + 80 + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const xPos = 8 + i * 144;
              return (
                <g key={i} opacity={s}>
                  <rect
                    x={xPos}
                    y={55}
                    width={110}
                    height={70}
                    rx="12"
                    fill="#0a2818"
                    stroke="#22c55e40"
                    strokeWidth="1.5"
                  />
                  <text
                    x={xPos + 55}
                    y={84}
                    fill="#4ade80"
                    fontSize="20"
                    fontWeight="700"
                    textAnchor="middle"
                  >
                    {rs.stage}
                  </text>
                  <text
                    x={xPos + 55}
                    y={108}
                    fill="#64748b"
                    fontSize="18"
                    textAnchor="middle"
                  >
                    {rs.time}
                  </text>
                  {i < p.responseStages.length - 1 && (
                    <line
                      x1={xPos + 114}
                      y1={90}
                      x2={xPos + 140}
                      y2={90}
                      stroke="#22c55e"
                      strokeWidth="2.5"
                      markerEnd="url(#arrowGreen)"
                    />
                  )}
                </g>
              );
            })}
            <defs>
              <marker
                id="arrowGreen"
                markerWidth="10"
                markerHeight="8"
                refX="10"
                refY="4"
                orient="auto"
              >
                <path d="M0,0 L10,4 L0,8 Z" fill="#22c55e" />
              </marker>
            </defs>
          </svg>
        </div>
      </div>

      {/* ===== CENTER: Vaccine Types + Antibody-Antigen SVG ===== */}
      <div style={{ position: 'absolute', top: 115, left: 645, width: 680 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          疫苗类型
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {p.vaccineTypes.map((vt, i) => {
            const s = spring({
              frame: frame - (vaccineStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: '#0a2818',
                  borderRadius: 14,
                  padding: '12px 14px',
                  border: '1.5px solid #22c55e20',
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: '#15803d30',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 26,
                    flexShrink: 0,
                  }}
                >
                  {vt.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#4ade80' }}>
                    {vt.name}
                  </div>
                  <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}>
                    {vt.mechanism}
                  </div>
                  <div style={{ fontSize: 22, color: '#64748b', marginTop: 2 }}>
                    {vt.example}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Antibody-Antigen Interaction SVG */}
        <div style={{ marginTop: 44 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: -40,
              letterSpacing: '0.08em',
            }}
          >
            抗体-抗原结合 · 免疫应答
          </div>
          <svg width="600" height="310" viewBox="0 0 780 310">
            {/* Antigen (virus-like shape) */}
            <g
              opacity={interpolate(
                frame,
                [svgStart, svgStart + 40],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              <circle cx="180" cy="140" r="60" fill="#dc262640" stroke="#f87171" strokeWidth="3" />
              {[0, 60, 120, 180, 240, 300].map((angle, i) => {
                const rad = (angle * Math.PI) / 180;
                const sx = 180 + 60 * Math.cos(rad);
                const sy = 140 + 60 * Math.sin(rad);
                const ex = 180 + 82 * Math.cos(rad);
                const ey = 140 + 82 * Math.sin(rad);
                return (
                  <g key={`spike-${i}`}>
                    <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#f87171" strokeWidth="3" />
                    <circle cx={ex} cy={ey} r="7" fill="#f87171" />
                  </g>
                );
              })}
              <text x="180" y="146" fill="#fca5a5" fontSize="28" fontWeight="700" textAnchor="middle">
                抗原
              </text>
            </g>

            {/* Antibody (Y-shape) approaching */}
            <g
              transform={`translate(${interpolate(
                frame,
                [svgStart + 30, svgStart + 80],
                [200, 0],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}, 0)`}
              opacity={antibodyDraw}
            >
              <path
                d="M460,210 L460,160 L410,95 M460,160 L510,95"
                fill="none"
                stroke="#22c55e"
                strokeWidth="5"
                strokeLinecap="round"
              />
              <circle cx="410" cy="88" r="20" fill="#22c55e40" stroke="#22c55e" strokeWidth="2.5" />
              <circle cx="510" cy="88" r="20" fill="#22c55e40" stroke="#22c55e" strokeWidth="2.5" />
              <text x="460" y="248" fill="#4ade80" fontSize="28" fontWeight="700" textAnchor="middle">
                抗体 (IgG)
              </text>
            </g>

            {/* Binding indicator */}
            <g
              opacity={interpolate(
                frame,
                [svgStart + 70, svgStart + 95],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              <line x1="262" y1="105" x2="390" y2="95" stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="8 4" />
              <text x="326" y="85" fill="#fbbf24" fontSize="26" fontWeight="700" textAnchor="middle">
                特异性结合
              </text>
            </g>

            {/* Memory cell formation */}
            <g
              opacity={interpolate(
                frame,
                [svgStart + 85, svgStart + 100],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              <rect x="600" y="80" width="165" height="100" rx="16" fill="#0a281840" stroke="#22c55e40" strokeWidth="2" />
              <text x="682" y="118" fill="#4ade80" fontSize="26" fontWeight="700" textAnchor="middle">
                记忆细胞
              </text>
              <text x="682" y="157" fill="#64748b" fontSize="26" textAnchor="middle">
                长期免疫保护
              </text>
              <line x1="530" y1="135" x2="598" y2="135" stroke="#22c55e" strokeWidth="2" strokeDasharray="6 3" markerEnd="url(#arrowSvg)" />
            </g>
            <defs>
              <marker id="arrowSvg" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
                <path d="M0,0 L10,4 L0,8 Z" fill="#22c55e" />
              </marker>
            </defs>
          </svg>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Milestones + Immune Facts ===== */}
      <div style={{ position: 'absolute', top: 115, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          疫苗里程碑
        </div>
        {p.milestones.map((m, i) => {
          const s = spring({
            frame: frame - (milestoneStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0a2818',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1.5px solid #15803d40',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: '#22c55e',
                    background: '#15803d30',
                    padding: '2px 10px',
                    borderRadius: 6,
                  }}
                >
                  {m.year}
                </span>
                <span style={{ fontSize: 24, fontWeight: 800, color: '#bbf7d0' }}>
                  {m.event}
                </span>
              </div>
              <div style={{ fontSize: 22, color: '#64748b', marginTop: 4 }}>
                {m.significance}
              </div>
            </div>
          );
        })}

        {/* Immune Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            免疫关键词
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.immuneFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0a2818',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #15803d40',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 4 }}>{fact.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#4ade80' }}>
                    {fact.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0a2818',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #15803d40',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 900, color: '#22c55e' }}>
                {st.value}
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#0a2818',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #15803d',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🧬</span>
          <span style={{ fontSize: 20, color: '#dcfce7', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #15803d, #22c55e, #bbf7d0)',
        }}
      />
    </AbsoluteFill>
  );
};
